import { isTruthy } from "@dashboard/library";

import {
  SearchConversationContentQuery,
  TranscriptionScopeFilter,
  TranscriptionScopeQueryType,
} from "@/graphql/generated/graphql";
import { SearchConversationContent } from "@/graphql/queries/SearchConversationContent";
import { NotFalsy } from "@/helpers/types/types.helpers";
import { useQueryWrapper } from "@/hooks/useQueryWrapper/useQueryWrapper";
import { useAdvanceSearchState } from "@/states/filters/advanced_search/advanced_search";
import { usePathParams } from "@/states/path_param";
import { useBaseStore } from "@/store/store";
import { TranscriptionScopeBuilder } from "../useFiltersVariables/useFiltersVariables.utility";
import { useHasCcAccess } from "../useHasCcAccess/useHasCcAccess";

const MAX_SEARCH_LIMIT = 100;

function pickType(
  query: string
): NotFalsy<TranscriptionScopeFilter["queryType"]>["type"] {
  switch (true) {
    case /\/conversations\/trackers/.test(document.referrer):
      return TranscriptionScopeQueryType.QUERYSTRING;
    case query.includes(" OR "):
      return TranscriptionScopeQueryType.QUERYSTRING;
    default:
      return TranscriptionScopeQueryType.MATCH;
  }
}

function formatSearchContentMatches(data?: SearchConversationContentQuery) {
  const call = data?.getCallConversations?.items?.[0];
  // `call` could be undefined if search term is not found in the current call
  const transcriptionScopeItems =
    call?.transcriptionScope?.items.filter(isTruthy) ?? [];

  return new Map(
    transcriptionScopeItems?.map((i) => [i.utterance.index, i.highlight])
  );
}

export function useSearchConversationContent() {
  const { callId } = usePathParams();
  const {
    advancedSearch: { query },
  } = useAdvanceSearchState();
  const hasCcAccess = useHasCcAccess();

  const setSearchContentMatches = useBaseStore(
    (store) => store.setSearchContentMatches
  );

  const transcriptionScope = new TranscriptionScopeBuilder();

  if (query && query.length > 2) {
    transcriptionScope.setSearchContent(query);
    transcriptionScope.setLimit(MAX_SEARCH_LIMIT);
    transcriptionScope.setQueryType({
      type: pickType(query),
    });
  }

  const transcriptionScopeValue = transcriptionScope.unwrap();

  useQueryWrapper(SearchConversationContent, {
    variables: {
      transcriptionScope: transcriptionScopeValue,
      callIds: [Number(callId)],
    },
    skip: !transcriptionScope.value.searchContent || !hasCcAccess,
    onCompleted: (data) => {
      setSearchContentMatches(formatSearchContentMatches(data));
    },
  });
}
